import { PlayerCommand } from "../engine/playerCommand";
import type { Player } from "../engine/player";
import { HeldKeyTracker } from "./heldKeyTracker";

const keyMap: Record<string, PlayerCommand> = {
	// WASD
	w: PlayerCommand.MOVE_N,
	a: PlayerCommand.MOVE_W,
	s: PlayerCommand.MOVE_S,
	d: PlayerCommand.MOVE_E,

	// QEZC diagonals
	q: PlayerCommand.MOVE_NW,
	e: PlayerCommand.MOVE_NE,
	z: PlayerCommand.MOVE_SW,
	c: PlayerCommand.MOVE_SE,

	// Arrow keys (4-dir)
	ArrowUp: PlayerCommand.MOVE_N,
	ArrowRight: PlayerCommand.MOVE_E,
	ArrowDown: PlayerCommand.MOVE_S,
	ArrowLeft: PlayerCommand.MOVE_W,

	// Interact
	Enter: PlayerCommand.INTERACT,

	// Numpad (8-dir)
	Numpad8: PlayerCommand.MOVE_N,
	Numpad9: PlayerCommand.MOVE_NE,
	Numpad6: PlayerCommand.MOVE_E,
	Numpad3: PlayerCommand.MOVE_SE,
	Numpad2: PlayerCommand.MOVE_S,
	Numpad1: PlayerCommand.MOVE_SW,
	Numpad4: PlayerCommand.MOVE_W,
	Numpad7: PlayerCommand.MOVE_NW,

	// Wait
	x: PlayerCommand.WAIT,
	".": PlayerCommand.WAIT,
	Numpad0: PlayerCommand.WAIT,
	Numpad5: PlayerCommand.WAIT,
	" ": PlayerCommand.WAIT,
};

// Build shift map: every movement key also shoots in that direction with Shift held
const shiftKeyMap: Record<string, PlayerCommand> = {};
for (const [key, cmd] of Object.entries(keyMap)) {
	if (cmd.startsWith("MOVE_")) {
		const shootCmd = ("SHOOT_" + cmd.slice(5)) as PlayerCommand;
		shiftKeyMap[key] = shootCmd;
	}
}
// Numpad shift entries use e.code, add them explicitly
for (const [key, cmd] of Object.entries(keyMap)) {
	if (key.startsWith("Numpad") && cmd.startsWith("MOVE_")) {
		const shootCmd = ("SHOOT_" + cmd.slice(5)) as PlayerCommand;
		shiftKeyMap[key] = shootCmd;
	}
}

// Number keys 1-7 → weapon family 1-7, key 0 → family 0 (dev/cheat)
const numberKeyToFamilyId: Record<string, number> = {
	"1": 1,
	"2": 2,
	"3": 3,
	"4": 4,
	"5": 5,
	"6": 6,
	"7": 7,
	"0": 0,
};

function resolveLookupKey(e: KeyboardEvent): string {
	const key = e.key;
	return e.code.startsWith("Numpad") ? e.code : key.length === 1 ? key.toLowerCase() : key;
}

function isRepeatableCommand(cmd: PlayerCommand): boolean {
	return cmd.startsWith("MOVE_") || cmd === PlayerCommand.WAIT;
}

export function setupInput(
	player: Player,
	onCommand: (cmd: PlayerCommand) => void,
	onWeaponSwitch?: () => void,
	onPause?: () => void,
	isModalOpen?: () => boolean,
	onModalDismiss?: () => void,
	isVendingOpen?: () => boolean,
	onVendingDismiss?: () => void,
	onHeldCommandChange?: (cmd: PlayerCommand | null) => void,
): () => void {
	const tracker = new HeldKeyTracker();
	const notifyHeld = (): void => onHeldCommandChange?.(tracker.getActive());

	const handler = (e: KeyboardEvent): void => {
		const key = e.key;

		// OS key-repeat never drives actions: the loop self-paces repeated movement from
		// held-key state. Only initial presses (e.repeat === false) fall through.
		if (e.repeat) {
			e.preventDefault();
			return;
		}

		// Vending overlay interception — swallows ALL keys.
		// Escape dismisses the overlay; everything else is eaten silently.
		if (isVendingOpen?.()) {
			e.preventDefault();
			if (key === "Escape") {
				onVendingDismiss?.();
			}
			return;
		}

		// Modal interception — when a modal UI is open:
		// - ENTER and ESCAPE dismiss the modal without queueing any action
		//   (ENTER would otherwise re-trigger the interact that opened it).
		// - Any other recognized input dismisses the modal AND falls through to
		//   normal handling, so the player can move/shoot/wait directly out of
		//   the modal without an extra keystroke.
		if (isModalOpen?.()) {
			if (key === "Escape" || key === "Enter") {
				e.preventDefault();
				onModalDismiss?.();
				return;
			}
			onModalDismiss?.();
			// fall through to normal handling
		}

		const keyLower = key.toLowerCase();

		// Escape — open/close pause menu, also cancel auto-move
		if (key === "Escape") {
			e.preventDefault();
			player.setAutoMoving(false);
			onPause?.();
			return;
		}

		// Tab — fire selected weapon at the nearest enemy (engine resolves the target).
		// preventDefault swallows the browser's focus-traversal default.
		if (key === "Tab") {
			e.preventDefault();
			player.setAutoMoving(false);
			onCommand(PlayerCommand.AUTO_SHOOT);
			return;
		}

		// Cancel auto-move on any game key
		player.setAutoMoving(false);

		// Use medikit (item) — H = Heal
		if (!e.shiftKey && keyLower === "h") {
			e.preventDefault();
			player.setUseItemCommand("Medikit");
			onCommand(PlayerCommand.USE_ITEM);
			return;
		}

		// Number-key weapon family selection (only without shift)
		if (!e.shiftKey && key in numberKeyToFamilyId) {
			e.preventDefault();
			player.selectWeaponFamily(numberKeyToFamilyId[key]);
			onWeaponSwitch?.();
			return;
		}

		// Movement / shooting — use e.code for Numpad keys, lowercase for single-char keys, raw for special keys
		const lookupKey = resolveLookupKey(e);

		if (e.shiftKey) {
			const cmd = shiftKeyMap[lookupKey];
			if (cmd === undefined) return;
			e.preventDefault();
			onCommand(cmd);
		} else {
			const cmd = keyMap[lookupKey];
			if (cmd === undefined) return;
			e.preventDefault();
			// Record the hold; the loop's heldCommand branch repeats it while the key stays down.
			if (isRepeatableCommand(cmd)) {
				tracker.press(lookupKey, cmd);
				notifyHeld();
			}
			onCommand(cmd);
		}
	};

	const keyupHandler = (e: KeyboardEvent): void => {
		tracker.release(resolveLookupKey(e));
		notifyHeld();
	};
	// Losing focus (alt-tab, tab switch) drops keyup events — clear held state so the player
	// doesn't keep walking after the key is physically released.
	const clearHeld = (): void => {
		tracker.clear();
		notifyHeld();
	};

	document.addEventListener("keydown", handler);
	document.addEventListener("keyup", keyupHandler);
	window.addEventListener("blur", clearHeld);
	document.addEventListener("visibilitychange", clearHeld);

	return () => {
		document.removeEventListener("keydown", handler);
		document.removeEventListener("keyup", keyupHandler);
		window.removeEventListener("blur", clearHeld);
		document.removeEventListener("visibilitychange", clearHeld);
	};
}
